import { useHookstate } from "@hookstate/core";
import { I18n } from "aws-amplify";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import React from "react";
import ConditionalComponent from "../ConditionalComponent";
import { installationRules } from "../Rules";
import { currentInstallation, getChaudList, scrbData, translateList, sortOptions, validateYear, getInstallationList } from "../ScrbTools";
import PropTypes from "prop-types";
import { Calendar } from "primereact/calendar";

Specifications.propTypes = {
    editable: PropTypes.bool.isRequired
}

function Specifications(props) {

    const installation = useHookstate(currentInstallation);
    const data = useHookstate(scrbData).get();

    const onObjectChange = (event, object) => {
        installation[object][event.target.name].set(event.target.value);
    }

    const onChange = (event) => {
        installation.set(prev => ({
            ...prev,
            [event.target.id]: event.target.value
        }));
    }

    const batimentPanel = () => {
        return (
            <div className="flex flex-column w-full pl-2 ">
                <h2 className="text-xl font-semibold mb-4">
                    <i className="fa-light fa-building mr-2"></i>
                    {I18n.get("batiment")}
                </h2>
                <div className="flex flex-column pt-2 gap-4">
                    <FloatLabel>
                        <InputText
                            id="specification"
                            value={installation.get().specification || ""}
                            onChange={onChange}
                            className={classNames("w-full")}
                            autoComplete="off"
                            disabled={!props.editable}
                            maxLength={60}
                        />
                        <label htmlFor="specification">{I18n.get("specification")}</label>
                    </FloatLabel>
                    <ConditionalComponent
                        objState={installation}
                        rules={installationRules}
                        id={["etage"]}
                        component={<>
                            <FloatLabel>
                                <InputNumber
                                    id="etage"
                                    value={installation.get().etage}
                                    onValueChange={onChange}
                                    className={classNames("w-full", { "p-invalid": [null, undefined].includes(installation.get().etage) })}
                                    inputStyle={{ width: '100%' }}
                                    autoComplete="off"
                                    disabled={!props.editable}
                                    maxLength={10}
                                />
                                <label htmlFor="etage">{I18n.get("etage")}</label>
                            </FloatLabel>
                            <FloatLabel>
                                <InputNumber
                                    id="nbEtages"
                                    value={installation.get().nbEtages}
                                    onValueChange={onChange}
                                    className={classNames("w-full")}
                                    inputStyle={{ width: '100%' }}
                                    autoComplete="off"
                                    min={0}
                                    disabled={!props.editable}
                                    maxLength={10}
                                />
                                <label htmlFor="nbEtages">{I18n.get("nbEtages")}</label>
                            </FloatLabel>
                        </>}
                    />
                    <FloatLabel>
                        <InputText
                            id="localEmplacement"
                            value={installation.get().localEmplacement || ""}
                            onChange={onChange}
                            className={classNames("w-full")}
                            autoComplete="off"
                            disabled={!props.editable}
                            maxLength={40}
                        />
                        <label htmlFor="localEmplacement">{I18n.get("localEmplacement")}</label>
                    </FloatLabel>
                    <FloatLabel>
                        <Dropdown
                            id="typeBatiment"
                            value={installation.get().typeBatiment}
                            onChange={(e) => installation["typeBatiment"].set(e.value)}
                            options={sortOptions(translateList(data.typeBatimentList))}
                            optionValue="id"
                            optionLabel="label"
                            placeholder={I18n.get("typeBatiment")}
                            className={classNames({
                                "w-full": true,
                                "p-invalid": !installation.get().typeBatiment
                            })}
                            autoComplete="off"
                            disabled={!props.editable}
                        />
                        <label htmlFor="typeBatiment">{I18n.get("typeBatiment")}</label>
                    </FloatLabel>
                    <FloatLabel>
                        <InputText
                            id="responsableNom"
                            value={installation.get().responsableNom || ""}
                            onChange={onChange}
                            className={classNames("w-full")}
                            autoComplete="off"
                            disabled={!props.editable}
                            maxLength={100}
                        />
                        <label htmlFor="responsableNom">{I18n.get("responsableNom")}</label>
                    </FloatLabel>
                    <ConditionalComponent
                        objState={installation}
                        rules={installationRules}
                        id={["pompeOnly"]}
                        component={<>
                            <FloatLabel>
                                <Calendar
                                    value={installation.get().anneeRenovBatiment}
                                    onChange={(e) => installation["anneeRenovBatiment"].set(e.target.value)}
                                    view="year"
                                    dateFormat="yy"
                                    maxDate={new Date()}
                                    className={classNames({
                                        "w-full": true,
                                        "p-invalid": [null, undefined].includes(installation.get().anneeRenovBatiment) ||
                                            !validateYear(installation.get().anneeRenovBatiment)
                                    })}
                                    disabled={!props.editable}
                                />
                                <label htmlFor="anneeRenovBatiment">{I18n.get("anneeRenovBatiment")}</label>
                            </FloatLabel>
                            <FloatLabel>
                                <InputNumber
                                    id="surfaceRefEnergitique"
                                    name="surfaceRefEnergitique"
                                    value={installation.get().surfaceRefEnergitique}
                                    onValueChange={onChange}
                                    className={classNames({
                                        "w-full": true,
                                        "p-invalid": [null, undefined].includes(installation.get().surfaceRefEnergitique)
                                    })}
                                    inputStyle={{ width: '100%' }}
                                    suffix="m²"
                                    min={1}
                                    autoComplete="off"
                                    disabled={!props.editable}
                                    maxLength={8}
                                />
                                <label htmlFor="surfaceRefEnergitique">{I18n.get("surfaceRefEnergitique")}</label>
                            </FloatLabel>
                        </>
                        }
                    />
                </div>
            </div>
        )
    }

    const appareilPanel = () => {
        return (
            <div className="flex flex-column w-full pt-5 pl-2">
                <h2 className="text-xl font-semibold mb-4">
                    <i className="fa-light fa-gear mr-2"></i>
                    {I18n.get("appareil")}
                </h2>
                <div className="flex flex-column pt-2 gap-4">
                    <ConditionalComponent
                        objState={installation}
                        rules={installationRules}
                        id={["climOnly"]}
                        component={<>
                            <FloatLabel>
                                <Dropdown
                                    name="typeCondenseur"
                                    value={installation.get().chaudiere.typeCondenseur}
                                    onChange={(e) => onObjectChange(e, "chaudiere")}
                                    options={sortOptions(translateList(data.typeCondenseurList))}
                                    optionValue="id"
                                    optionLabel="label"
                                    placeholder={I18n.get("typeCondenseur")}
                                    className={classNames({
                                        "w-full": true,
                                        "p-invalid": !installation.get().chaudiere.typeCondenseur
                                    })}
                                    disabled={!props.editable}
                                />
                                <label htmlFor="typeCondenseur">{I18n.get("typeCondenseur")}</label>
                            </FloatLabel>
                            <FloatLabel>
                                <Dropdown
                                    name="typeEvaporateur"
                                    value={installation.get().chaudiere.typeEvaporateur}
                                    onChange={(e) => onObjectChange(e, "chaudiere")}
                                    options={sortOptions(translateList(data.typeEvaporateurList))}
                                    optionValue="id"
                                    optionLabel="label"
                                    placeholder={I18n.get("typeEvaporateur")}
                                    className={classNames({
                                        "w-full": true,
                                        "p-invalid": !installation.get().chaudiere.typeEvaporateur
                                    })}
                                    disabled={!props.editable}
                                />
                                <label htmlFor="typeEvaporateur">{I18n.get("typeEvaporateur")}</label>
                            </FloatLabel>
                            <FloatLabel>
                                <Dropdown
                                    name="utilisation"
                                    value={installation.get().utilisation}
                                    onChange={(e) => installation["utilisation"].set(e.value)}
                                    options={sortOptions(translateList(data.utilisationList))}
                                    optionLabel="label"
                                    optionValue="id"
                                    placeholder={I18n.get("utilisation")}
                                    className={classNames({
                                        "w-full": true,
                                        "p-invalid": !installation.get().utilisation
                                    })}
                                    disabled={!props.editable}
                                />
                                <label htmlFor="utilisation">{I18n.get("utilisation")}</label>
                            </FloatLabel>
                            <FloatLabel>
                                <Dropdown
                                    name="typeFluide"
                                    value={installation.get().chaudiere.typeFluide}
                                    onChange={(e) => onObjectChange(e, "chaudiere")}
                                    options={sortOptions(data.typeFluideList)}
                                    optionLabel="label"
                                    optionValue="label"
                                    placeholder={I18n.get("typeFluide")}
                                    filter
                                    className={classNames({
                                        "w-full": true,
                                        "p-invalid": !installation.get().chaudiere.typeFluide
                                    })}
                                    disabled={!props.editable}
                                />
                                <label htmlFor="typeFluide">{I18n.get("typeFluide")}</label>
                            </FloatLabel>
                            <FloatLabel>
                                <InputText
                                    id="fluideCaloporteur"
                                    name="fluideCaloporteur"
                                    value={installation.get().chaudiere.fluideCaloporteur || ""}
                                    onChange={(e) => onObjectChange(e, "chaudiere")}
                                    className={classNames("w-full")}
                                    autoComplete="off"
                                    disabled={!props.editable}
                                    maxLength={20}
                                />
                                <label htmlFor="fluideCaloporteur">{I18n.get("fluideCaloporteur")}</label>
                            </FloatLabel>
                        </>}
                    />
                    <ConditionalComponent
                        objState={installation}
                        rules={installationRules}
                        id={["typeInstallation"]}
                        component={(
                            <FloatLabel>
                                <Dropdown
                                    value={installation.get().typeInstallation}
                                    onChange={(e) => installation["typeInstallation"].set(e.value)}
                                    options={sortOptions(translateList(getInstallationList(installation.get().genreInstallationId)))}
                                    optionValue="id"
                                    optionLabel="label"
                                    placeholder={I18n.get("typeInstallation")}
                                    className={classNames({
                                        "w-full": true,
                                        "p-invalid": !installation.get().typeInstallation
                                    })}
                                    disabled={!props.editable}
                                />
                                <label htmlFor="typeInstallation">{I18n.get("typeInstallation")}</label>
                            </FloatLabel>
                        )}
                    />
                    <ConditionalComponent
                        objState={installation}
                        rules={installationRules}
                        id={["typeCombustible"]}
                        component={(
                            <FloatLabel>
                                <Dropdown
                                    value={installation.get().combustible}
                                    onChange={(e) => installation["combustible"].set(e.value)}
                                    options={sortOptions(translateList(JSON.parse(JSON.stringify(data.typeCombustibleList)).filter(item => item.genreInst === installation.get().genreInstallationId)))}
                                    optionLabel="label"
                                    optionValue="id"
                                    placeholder={I18n.get("combustible")}
                                    filter
                                    className={classNames({
                                        "w-full": true,
                                        "p-invalid": !installation.get().combustible
                                    })}
                                    disabled={!props.editable}
                                />
                                <label htmlFor="combustible">{I18n.get("combustible")}</label>
                            </FloatLabel>
                        )}
                    />
                    <ConditionalComponent
                        objState={installation}
                        rules={installationRules}
                        id={["pompeOnly"]}
                        component={<>
                            <FloatLabel>
                                <Dropdown
                                    name="typeChaudiere"
                                    value={installation.get().chaudiere.typeChaudiere}
                                    onChange={(e) => onObjectChange(e, "chaudiere")}
                                    options={sortOptions(translateList(getChaudList(installation.get().genreInstallationId)))}
                                    optionValue="id"
                                    optionLabel="label"
                                    placeholder={I18n.get("typeChaudiere")}
                                    className={classNames({
                                        "w-full": true,
                                        "p-invalid": !installation.get().chaudiere.typeChaudiere
                                    })}
                                    disabled={!props.editable}
                                />
                                <label htmlFor="typeChaudiere">{I18n.get("typePompe")}</label>
                            </FloatLabel>
                            <FloatLabel>
                                <Dropdown
                                    name="typeFluide"
                                    value={installation.get().chaudiere.typeFluide}
                                    onChange={(e) => onObjectChange(e, "chaudiere")}
                                    options={sortOptions(data.typeFluideList)}
                                    optionLabel="label"
                                    optionValue="id"
                                    placeholder={I18n.get("typeFluide")}
                                    filter
                                    className={classNames({
                                        "w-full": true,
                                        "p-invalid": !installation.get().chaudiere.typeFluide
                                    })}
                                    disabled={!props.editable}
                                />
                                <label htmlFor="typeFluide">{I18n.get("typeFluide")}</label>
                            </FloatLabel>
                            <FloatLabel>
                                <InputNumber
                                    id="qteRemplissageRefrigerant"
                                    name="qteRemplissageRefrigerant"
                                    value={installation.get().chaudiere.qteRemplissageRefrigerant}
                                    onValueChange={(e) => onObjectChange(e, "chaudiere")}
                                    className={classNames({
                                        "w-full": true,
                                        "p-invalid": [null, undefined].includes(installation.get().chaudiere.qteRemplissageRefrigerant)
                                    })}
                                    inputStyle={{ width: '100%' }}
                                    suffix="KG"
                                    min={0}
                                    maxFractionDigits={1}
                                    autoComplete="off"
                                    disabled={!props.editable}
                                    maxLength={52}
                                />
                                <label htmlFor="qteRemplissageRefrigerant">{I18n.get("qteRemplissageRefrigerant")}</label>
                            </FloatLabel>
                            <FloatLabel>
                                <Dropdown
                                    name="modeFonctionnementPompe"
                                    value={installation.get().chaudiere.modeFonctionnementPompe}
                                    onChange={(e) => onObjectChange(e, "chaudiere")}
                                    options={sortOptions(translateList(data.modeFonctionnementPompeList))}
                                    optionLabel="label"
                                    optionValue="id"
                                    placeholder={I18n.get("modeFonctionnementPompe")}
                                    className={classNames({
                                        "w-full": true,
                                        "p-invalid": [null, undefined].includes(installation.get().chaudiere.modeFonctionnementPompe)
                                    })}
                                    disabled={!props.editable}
                                />
                                <label htmlFor="modeFonctionnementPompe">{I18n.get("modeFonctionnementPompe")}</label>
                            </FloatLabel>
                            <ConditionalComponent
                                objState={installation}
                                rules={installationRules}
                                id={["modeFonctionnementPompe2"]}
                                object="chaudiere"
                                targetField={[{ field: "puissanceResistance", clearanceValue: null }]}
                                component={
                                    <FloatLabel>
                                        <InputNumber
                                            id="puissanceResistance"
                                            name="puissanceResistance"
                                            value={installation.get().chaudiere.puissanceResistance}
                                            onValueChange={(e) => onObjectChange(e, "chaudiere")}
                                            className={classNames({
                                                "w-full": true,
                                                "p-invalid": [null, undefined].includes(installation.get().chaudiere.puissanceResistance)
                                            })}
                                            inputStyle={{ width: '100%' }}
                                            minFractionDigits={1}
                                            maxFractionDigits={1}
                                            suffix="kW"
                                            min={0}
                                            autoComplete="off"
                                            disabled={!props.editable}
                                            maxLength={52}
                                        />
                                        <label htmlFor="puissanceResistance">{I18n.get("puissanceResistance")}</label>
                                    </FloatLabel>
                                }
                            />
                            <ConditionalComponent
                                objState={installation}
                                rules={installationRules}
                                id={["modeFonctionnementPompe3"]}
                                object="chaudiere"
                                targetField={[{ field: "puissanceDeuxiemeGenerateur", clearanceValue: null }]}
                                component={
                                    <FloatLabel>
                                        <InputNumber
                                            id="puissanceDeuxiemeGenerateur"
                                            name="puissanceDeuxiemeGenerateur"
                                            value={installation.get().chaudiere.puissanceDeuxiemeGenerateur}
                                            onValueChange={(e) => onObjectChange(e, "chaudiere")}
                                            className={classNames({
                                                "w-full": true,
                                                "p-invalid": [null, undefined].includes(installation.get().chaudiere.puissanceDeuxiemeGenerateur)
                                            })}
                                            inputStyle={{ width: '100%' }}
                                            minFractionDigits={1}
                                            maxFractionDigits={1}
                                            suffix="kW"
                                            min={0}
                                            autoComplete="off"
                                            disabled={!props.editable}
                                            maxLength={52}
                                        />
                                        <label htmlFor="puissanceDeuxiemeGenerateur">{I18n.get("puissanceDeuxiemeGenerateur")}</label>
                                    </FloatLabel>
                                }
                            />
                        </>}
                    />
                </div>
            </div>
        )
    }

    return (

        <div className="flex flex-column pt-5">
            <div className="flex flex-column">
                {batimentPanel()}
                {appareilPanel()}
            </div>
        </div>
    );
}

export default Specifications;